import { useEffect, useState, useCallback } from "react";
import { getWindGrid, getScalarGrid, getFires } from "../lib/api";

const padded = (b, f = 0.25) => {
  const padLat = (b.latMax - b.latMin) * f;
  const padLon = (b.lonMax - b.lonMin) * f;
  return { lat_min: b.latMin - padLat, lat_max: b.latMax + padLat, lon_min: b.lonMin - padLon, lon_max: b.lonMax + padLon };
};

const useBoundsFetch = (bounds, enabled, fetcher, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || !bounds) return;
    let alive = true;
    const t = setTimeout(() => {
      setLoading(true);
      setError(false);
      fetcher(padded(bounds))
        .then((d) => alive && setData(d))
        .catch(() => alive && setError(true))
        .finally(() => alive && setLoading(false));
    }, 800);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds, enabled, attempt, ...deps]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { data, loading, error, retry };
};

export const useWindGrid = (bounds, enabled) => {
  const { data, loading, error, retry } = useBoundsFetch(bounds, enabled, (b) => getWindGrid(b));
  return { grid: data, loading, error, retry };
};

export const useScalarGrid = (bounds, variable, enabled) => {
  const { data, loading, error, retry } = useBoundsFetch(
    bounds,
    enabled,
    (b) => getScalarGrid({ ...b, var: variable, nx: 10, ny: 7 }),
    [variable]
  );
  return { grid: data && data.var === variable ? data : null, loading, error, retry };
};

export const useFires = (bounds, days, enabled) => {
  const { data, loading, error, retry } = useBoundsFetch(bounds, enabled, (b) => getFires({ ...b, days }), [days]);
  return { fires: data, loading, error, retry };
};
